<template>
  <div>
    <PositionWarning
      :show="showPositionWarning"
      :message="positionWarning"
      @dismiss="dismissWarning"
    />

    <div class="layout-sidebar-section">
      <div
        class="layout-sidebar-section-title"
        @click="$emit('toggleSection', 'textEditorSection')"
      >
        <strong>Text Editor</strong>
        <b-icon :icon="isOpen.textEditorSection ? 'chevron-up' : 'chevron-down'" />
      </div>
      <transition>
        <b-collapse :visible="isOpen.textEditorSection">
          <div>
            <FormGroupCharLimit
              label="Text"
              :value="text"
              :max="maxChars"
              max-editable
              copy
              @change="onChange('maxChar', $event)"
              @copy="onCopy"
            >
              <template #default="{ state }">
                <!-- TEXT INPUT / TEXTAREA -->
                <b-textarea
                  v-if="isMultiLineField"
                  ref="textEdit"
                  :value="text"
                  :state="state"
                  :disabled="isEditingDisabled"
                  rows="8"
                  @input="handleTextInput"
                  @blur="onBlurText"
                />
                <b-input
                  v-else
                  ref="textEdit"
                  :value="text"
                  :state="state"
                  :disabled="isEditingDisabled"
                  @input="handleTextInput"
                />
                <b-form-group
                  v-if="target.isTocLine"
                  label="Page Number"
                  class="mt-2 mb-2"
                >
                  <b-input
                    :value="target.tocPageNumber"
                    :disabled="isEditingDisabled"
                    @input="onChange('tocPageNumber', $event)"
                  />
                </b-form-group>
                <div class="d-flex align-items-center justify-content-between w-100 color-checkbox-container">
                  <ColorPicker
                    v-if="options.colorSelector"
                    :value="target.fontColor"
                    :themes="themes"
                    :is-text-editor="true"
                    position="top-right"
                    copy
                    @input="onChange('fontColor', $event)"
                  />

                  <div class="text-case-checkboxes">
                    <b-checkbox
                      v-model="titleCase"
                      @change="toggleTitleCase"
                    >
                      Title Case
                    </b-checkbox>

                    <b-checkbox
                      v-model="allCaps"
                      @change="toggleAllCaps"
                    >
                      All Caps
                    </b-checkbox>
                  </div>
                </div>

                <b-button
                  v-if="isMultiLineField && hasMultipleLines"
                  variant="outline-secondary"
                  size="sm"
                  class="mt-2 w-100"
                  :disabled="isSeparating"
                  @click="separateIntoTextBlocks"
                >
                  <b-spinner
                    v-if="isSeparating"
                    small
                    class="mr-1"
                  />
                  <b-icon
                    v-else
                    icon="layout-split"
                    class="mr-1"
                  />
                  Separate into separate text blocks
                </b-button>

                <ChatGPTForm
                  :value="target.text"
                  class="flex-grow-1 d-flex mt-2"
                  @change="onChange"
                />
                <!-- EXTEND CONTAINER -->
                <ExtendContainerRow
                  v-if="isMultiLineField && options.extendContainer"
                  class="mt-2"
                  :target="target"
                  :config="options.extendContainer"
                  @data-updated="updateContainerSettings"
                  @extend-container="onExtendContainer"
                />
              </template>
            </FormGroupCharLimit>
          </div>
        </b-collapse>
      </transition>
    </div>
    <div class="layout-sidebar-section">
      <div
        class="layout-sidebar-section-title"
        @click="$emit('toggleSection', 'textSettingsSection')"
      >
        <strong>Text Settings</strong>
        <b-icon :icon="isOpen.textSettingsSection ? 'chevron-up' : 'chevron-down'" />
      </div>
      <transition>
        <b-collapse :visible="isOpen.textSettingsSection">
          <div>
            <b-form-group
              v-if="themeStyles"
              label="Text Style"
            >
              <Multiselect
                v-model="selectedStyle"
                placeholder="Select style"
                track-by="id"
                label="name"
                :options="groupedThemeStyles"
                group-label="name"
                group-values="children"
                :group-select="false"
                :multi="false"
                :allow-empty="false"
                select-label=""
                deselect-label=""
                @input="applyTextStyle"
              >
                <template #option="props">
                  <span
                    v-if="!props.option.$isLabel"
                    :style="getStylePreview(props.option)"
                  >{{ getStyleOptionName(props.option) }}</span>
                  <span
                    v-else
                    :style="getStylePreview(props.option)"
                    class="themes-group-label"
                  >{{ props.option.$groupLabel }}</span>
                </template>
                <template #singleLabel="props">
                  <span
                    v-if="props.option.$isLabel"
                    :style="{ fontWeight: 'bold' }"
                  >{{ props.option.$groupLabel }}</span>
                  <span
                    v-else
                    :style="getStylePreview(props.option)"
                  >{{ getStyleOptionName(props.option) }}</span>
                </template>
              </Multiselect>
            </b-form-group>


            <b-form-group
              v-if="options.fontSelector"
              label="Font"
            >
              <Multiselect
                v-model="fontVal"
                placeholder="Font"
                track-by="value"
                label="name"
                :options="getFonts"
                :allow-empty="false"
                @select="handleFontChange"
              >
                <template #option="props">
                  <div
                    :style="{ fontFamily: props.option.value }"
                    :class="{
                      'font-used-in-theme':fontsUsedInStyles.includes(props.option.value),
                      'theme-font-separator': props.option.isLastUsed
                    }"
                  >
                    {{ props.option.name }}
                  </div>
                </template>
                <template #singleLabel="props">
                  <span :style="{ fontFamily: props.option.value }">{{ props.option.name }}</span>
                </template>
              </Multiselect>
              <small
                v-if="options.fontSelector && (!target.fontFamily || !fontVal)"
                class="text-warning"
              >
                <i class="fas fa-exclamation-triangle mr-1" />
                Please select a font for this text field
              </small>
            </b-form-group>

            <b-form-group>
              <template #label>
                <div class="d-flex align-items-center justify-content-between">
                  <span>Alignment</span>
                  <img
                    v-b-tooltip.hover
                    src="/img/icons/text-fit-width.png"
                    alt="Set field to text width"
                    title="Set field to text width"
                    style="height: 16px; cursor: pointer;"
                    @mousedown.prevent
                    @click="onFitTextWidthClick"
                  >
                </div>
              </template>


              <b-form-select
                :value="target.textAlign"
                :options="getAlignOptions"
                @change="onChange('textAlignment', $event)"
              />
            </b-form-group>

            <b-form-row>
              <b-col>
                <b-form-group
                  label="Size"
                  :disabled="target.locked"
                >
                  <b-form-input
                    :value="config.fontSize"
                    type="number"
                    min="0"
                    @input="onChange('fontSize', $event)"
                    @dblclick="selectValueOnDblclick"
                    @keypress="preventNegativeInput"
                  />
                </b-form-group>
              </b-col>

              <b-col>
                <b-form-group label="Opacity">
                  <b-form-input
                    :value="target.opacity"
                    type="number"
                    min="0"
                    step="0.01"
                    max="1"
                    @input="onChange('opacity', $event)"
                    @dblclick="selectValueOnDblclick"
                    @keypress="preventNegativeInput"
                  />
                </b-form-group>
              </b-col>
            </b-form-row>

            <div class="d-flex align-items-center">
              <b-form-checkbox
                :checked="target.isCurved"
                class="me-3"
                name="check-button"
                switch
                @change="onChange('changeCurved', !target.isCurved)"
              >
                Curved
              </b-form-checkbox>

              <b-form-checkbox
                v-if="target.isCurved"
                class="ml-1"
                :checked="target.flipped"
                name="check-button"
                switch
                @change="onChange('changeFlipped', !target.flipped)"
              >
                Flipped
              </b-form-checkbox>
            </div>
            <b-form-group
              v-if="target.isCurved"
              label=""
            >
              <b-input-group>
                <b-input
                  min="5"
                  max="3000"
                  step="0.001"
                  type="range"
                  :value="target.curvedDiameter"
                  @input="onChangeThrottle('changeRadius', $event)"
                />
                <template #append>
                  <b-input
                    min="0"
                    max="1"
                    step="0.001"
                    type="number"
                    :value="diameterToRatio(target.curvedDiameter)"
                    :formatter="betweenZeroAndOneFormatter"
                    @input="onRatioInput($event)"
                  />
                </template>
              </b-input-group>
            </b-form-group>
          </div>
        </b-collapse>
      </transition>
    </div>
    <PositionAndSize
      :x="target.left"
      :y="target.top"
      :w="config.width"
      :h="config.height"
      :is-open="isOpen"
      @toggleSection="toggleSectionHandler"
      @input="onChange"
    />
    <div class="layout-sidebar-section">
      <div
        class="layout-sidebar-section-title"
        @click="$emit('toggleSection', 'actionSection')"
      >
        <strong>Actions</strong>
        <b-icon :icon="isOpen.actionSection ? 'chevron-up' : 'chevron-down'" />
      </div>
      <transition>
        <b-collapse :visible="isOpen.actionSection">
          <div>
            <div
              class="text-editor-actions d-flex my-2"
            >
              <DropdownCounter
                :value="target.charSpacing"
                title="Text Spacing"
                :step="10"
                @input="onChange('textSpacing', $event)"
              >
                <i class="glyphicon glyphicon-text-width" />
              </DropdownCounter>

              <div
                v-if="fontFamilyWeights.length"
                class="dropdown"
              >
                <button
                  aria-expanded="false"
                  aria-haspopup="true"
                  class="dropdown-toggle d-flex align-items-center h-100"
                  data-toggle="dropdown"
                  title="Tracking"
                  type="button"
                >
                  <i class="glyphicon glyphicon glyphicon-bold" />
                </button>
                <div class="dropdown-menu menu-wide p-0 w-100">
                  <button
                    v-for="(font, index) in fontFamilyWeights"
                    :key="index"
                    :style="{ 'font-family': font.index }"
                    class="dropdown-item p-2"
                    type="button"
                    @click="onChangeTextWeight(font.index)"
                  >
                    {{ TEXT_WEIGHT[font.weight] }}
                  </button>
                </div>
              </div>
              <LineHeightDropdown
                :value="target.lineHeight"
                @input="onChange('textLeading', $event)"
              >
                <i class="cil-line-spacing" />
              </LineHeightDropdown>
              <button
                v-b-tooltip.hover
                title="Set to template width"
                @click="onChange('setTemplateSizeByWidth')"
              >
                <img src="/img/icons/expand.svg">
              </button>
              <button
                v-b-tooltip.hover
                title="Center horizontally"
                @click="onChange('centerToTemplateWidth')"
              >
                <img
                  src="/img/icons/align-center.png"
                  alt="align-center-icon"
                >
              </button>
              <button
                v-b-tooltip.hover
                title="Center vertically"
                @click="onChange('centerToTemplateHeight')"
              >
                <img
                  src="/img/icons/vertical-alignment.png"
                  alt="align-center-icon"
                >
              </button>
              <button
                v-b-tooltip.hover
                title="Left align to template"
                @click="onChange('leftAlignToTemplate')"
              >
                <img
                  src="/img/icons/align-left.png"
                  alt="align-left-icon"
                >
              </button>
              <button
                v-b-tooltip.hover
                title="Right align to template"
                @click="onChange('rightAlignToTemplate')"
              >
                <img
                  src="/img/icons/align-right.png"
                  alt="align-right-icon"
                >
              </button>
              <DropdownSlider
                label="Size"
                tooltip="Set superscript"
                :value="config.fontSize"
                @change="onChange('superscript', $event)"
              >
                <template #placeholder>
                  <i class="fa fa-superscript" />
                </template>
              </DropdownSlider>
              <button
                v-b-tooltip.hover
                title="Set default superscript"
                @click="onChange('superscriptDefault')"
              >
                <i class="fa fa-superscript" />
              </button>
              <button
                v-b-tooltip.hover
                title="Set default subscript"
                @click="onChange('subscriptDefault')"
              >
                <i class="fa fa-subscript" />
              </button>
              <button
                v-b-tooltip.hover
                title="Set regular"
                @click="onChange('resetFontSize')"
              >
                <i class="fa fa-heading" />
              </button>
            </div>

            <div class="position-fixed apply-to-all-text-dropdwon-section">
              <ApplyToAllTextDropdown
                :options="options"
                :target="target"
              />
            </div>
          </div>
        </b-collapse>
      </transition>
    </div>
    <div class="layout-sidebar-section">
      <div
        class="layout-sidebar-section-title"
        @click="$emit('toggleSection', 'relatedFieldSection')"
      >
        <strong>Related Field</strong>
        <b-icon :icon="isOpen.relatedFieldSection ? 'chevron-up' : 'chevron-down'" />
      </div>
      <transition>
        <b-collapse :visible="isOpen.relatedFieldSection">
          <div>
            <b-form-row>
              <b-col>
                <b-form-group
                  label="Related field"
                >
                  <b-form-select
                    :value="target.relatedField?.field"
                    :options="relatedTemplateFields"
                    @change="onChange('changeRelatedField', $event)"
                  />
                </b-form-group>
              </b-col>

              <b-col>
                <b-form-group label="Margin">
                  <b-form-input
                    :value="target.relatedField?.margin"
                    type="number"
                    min="0"
                    step="1"
                    @input="onChange('changeMargin', $event)"
                    @dblclick="selectValueOnDblclick"
                    @keypress="preventNegativeInput"
                  />
                </b-form-group>
              </b-col>
            </b-form-row>
            
            <b-form-row>
              <div
                v-if="isMultiLineField"
                class="d-flex align-items-baseline"
              >
                <div class="btn-group">
                  <b-button
                    class="align-to-btn mt-1 btn-sm"
                    variant="primary"
                    data-action="align-to-btn"
                    @click="redraw()"
                  >
                    <span v-if="target.alignToField">Align to <strong>{{ target.alignToField }}</strong></span>
                    <span v-else>Select a field for text alignment</span>
                  </b-button>

                  <b-dropdown
                    ref="alignToFieldDropdown"
                    class="mt-1 align-options-dropdown"
                    variant="primary"
                    right
                    no-caret
                  >
                    <template #button-content>
                      <i class="fas fa-cog" />
                    </template>
                    <b-dropdown-form>
                      <b-form-radio-group
                        v-model="shouldMaximizeFontSize"
                        class="mb-2"
                      >
                        <b-form-radio :value="false">
                          Keep font size
                        </b-form-radio>
                        <b-form-radio :value="true">
                          Maximize font size
                        </b-form-radio>
                      </b-form-radio-group>
                      <b-form-checkbox
                        v-model="keepLineBreaks"
                      >
                        Keep line breaks
                      </b-form-checkbox>

                      <b-form-checkbox
                        v-if="shouldMaximizeFontSize"
                        v-model="useTemplateFontSize"
                      >
                        Use template font size as max
                      </b-form-checkbox>
                      <b-form-select
                        :value="target.alignToField"
                        :options="alignToOptions"
                        size="sm"
                        class="mt-3"
                        @change="onChange('changeAlignToField', $event)"
                      />
                    </b-dropdown-form>
                  </b-dropdown>
                </div>

                <b-checkbox
                  v-show="false"
                  class="ml-1"
                  :checked="getAutoPositionValue(target)"
                  @change="onChangeAutoPositionValue($event)"
                >
                  Auto position
                </b-checkbox>
              </div>
            </b-form-row>
          </div>
        </b-collapse>
      </transition>
    </div>
    <!--<div class="layout-sidebar-section">
      <div
        class="layout-sidebar-section-title"
        @click="$emit('toggleSection', 'suggestionSection')"
      >
        <strong>Suggestion Area</strong>
        <b-icon :icon="isOpen.suggestionSection ? 'chevron-up' : 'chevron-down'" />
      </div>
      <transition>
        <b-collapse :visible="isOpen.suggestionSection">
          <div>
            <ChatGPTForm
              :value="target.text"
              class="flex-grow-1 d-flex mt-2"
              @change="onChange"
            />
          </div>
        </b-collapse>
      </transition>
    </div>-->

    <b-button
      variant="danger"
      @click="onChange('delete')"
    >
      <b-icon icon="trash" />
      Delete
    </b-button>

    <LockToggle />
  </div>
</template>

<script>
/* global fontFamilyManager */
import ColorPicker from './components/ColorPicker.vue'
import {FIELD_TYPE_MULTI_LINE_TEXT,} from '@frontend/constants/type-fields-of-template'
import {TEXT_WEIGHT} from '@frontend/constants/text-weight'
import DropdownCounter from './components/DropdownCounter.vue'
import {parseStyleToText} from '@/common/preview/parse-style-to-text'
import {editorControlEventBroker, EVENTS} from '@frontend/group/modules/editor-control-popup/event-broker'
import LineHeightDropdown from './components/LineHeightDropdown.vue'
import ChatGPTForm from './components/ChatGPTForm.vue'
import Multiselect from 'vue-multiselect'
import PositionAndSize from './components/PositionAndSize.vue'
import LockToggle from '@frontend/components/modules/layouts-page/EditorControl/components/LockToggle.vue'
import mixinInput from '@frontend/components/input-mixins'
import FormGroupCharLimit from '@frontend/components/common/atoms/FormGroupCharLimit.vue'
import ApplyToAllTextDropdown
  from '@frontend/components/modules/layouts-page/EditorControl/components/ApplyToAllTextDropdown.vue';
import {MULTI_LINE_ALIGNMENTS, TEXT_ALIGNMENTS} from '@frontend/constants/alignment'
import {mapGetters} from 'vuex';
import {RELATED_FIELD_TYPES} from "@frontend/constants/related-fields";
import ExtendContainerRow
  from '@frontend/components/modules/layouts-page/EditorControl/components/ExtendContainerRow.vue';
import warningMixin from '@frontend/mixins/warningMixin';
import PositionWarning from '@frontend/components/shared/PositionWarning.vue'
import {ALIGN_TO_FIELD_TYPES} from "@frontend/constants/align-to-fields";
import {
  ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID,
  ADDITIONAL_RECTANGLE_PREFIX_ELEMENT_ID
} from "@frontend/constants/templates-field-prefixes";
import {ThemesContainerSingleton} from "@/banner_template/themes/ThemesContainerSinglton";
import { COPY_PASTE_EVENTS, copyPasteEventBroker } from "@frontend/group/modules/copy-paste-object/event-broker";
import { editModeService } from "@frontend/services/EditModeService";
import DropdownSlider from "@frontend/components/DropdownSlider";
import { addAddField } from '@frontend/group/modules/additional-fields';

export default {
  components: {
    DropdownSlider,
    ApplyToAllTextDropdown,
    FormGroupCharLimit,
    LockToggle,
    PositionAndSize,
    Multiselect,
    ColorPicker,
    DropdownCounter,
    LineHeightDropdown,
    ChatGPTForm,
    ExtendContainerRow,
    PositionWarning,
  },
  mixins: [mixinInput, warningMixin],
  props: {
    target: {
      type: fabric.Object,
      require: true,
      default: () => {},
    },
    options: {
      type: Object,
      default: () => {},
    },
    themes: {
      type: Array,
      default: () => [],
    },
    fieldType: {
      type: String,
      require: true,
      default: '',
    },
    isOpen: {
      type: Object,
      default: () => ({
        textEditorSection: true,
        textSettingsSection: true,
        actionSection: true,
        relatedFieldSection: true,
      }),
    },
  },
  data () {
    return {
      config: {width: 0, height: 0, fontSize: 0},
      TEXT_WEIGHT,
      chatGPTInput: '',
      isPinProperties: false,
      isApplyToAllUpdate: true,
      isApplyToAllAdd: false,
      fontFamily: [],
      textAlignments: TEXT_ALIGNMENTS,
      multiLineAlignments: MULTI_LINE_ALIGNMENTS,
      fontFamilyWeights: [],
      selectedFontFamily: '',
      titleCase: false,
      allCaps: false,
      shouldMaximizeFontSize: false,
      keepLineBreaks: true,
      useTemplateFontSize: false,
      fontVal: '',
      selectedStyle: null,
      isSeparating: false,
      targetSelectionChangedHandler: null,
      targetScalingHandler: null,
    }
  },
  computed: {
    ...mapGetters('layout', [
      'selectedInstance',
    ]),
    text () {
      return parseStyleToText(this.target.textLines, this.target.styles)
    },
    isMultiLineField () {
      return [FIELD_TYPE_MULTI_LINE_TEXT].includes(this.fieldType)
    },
    hasMultipleLines () {
      const lines = this.target?.text?.split('\n') || [];
      return lines.filter(l => l.trim().length > 0).length > 1;
    },
    getAlignOptions() {
      if (this.isMultiLineField) {
        return this.multiLineAlignments;
      }
      
      return this.textAlignments;
    },
    getFonts () {
      const fonts = []
      const theme = ThemesContainerSingleton.getByIdSync(this.selectedInstance.data.theme);
      /* const allFonts = theme
        ? filterFontsByThemeFonts(this.options.fonts, theme, [this.target.fontFamily])
        : this.options.fonts;*/
      const allFonts = this.options.fonts;
       

      // getting all fonts used in theme styles
      const fontsInStyles = theme ? theme.getFonts() : [];

      const usedFontsSet = new Set(fontsInStyles);

      const usedFonts = [];
      const unusedFonts = [];

      for (const key in allFonts) {
        const font = allFonts[key];
        const selectedFontFamily = this.target.fontFamily;

        const fontName = Array.isArray(font)
          ? (font.includes(selectedFontFamily) ? selectedFontFamily : font[0])
          : key;

        const fontObj = {
          name: Array.isArray(font) ? key : font,
          value: fontName,
          isUsed: usedFontsSet.has(fontName),
        };

        if (fontObj.isUsed) {
          usedFonts.push(fontObj);
        } else {
          unusedFonts.push(fontObj);
        }
      }

      // Mark last used
      if (usedFonts.length) {
        usedFonts[usedFonts.length - 1].isLastUsed = true;
      }

      return [...usedFonts, ...unusedFonts];
    },
    themeStyles() {
      const theme = ThemesContainerSingleton.getByIdSync(this.selectedInstance.data.theme);
      let styles = theme?.attributes?.find(attr => _.get(attr, 'name') === 'Styles')?.list || [];
      if (!styles?.length && window.selectedTheme) {
        styles = window.selectedTheme?.attributes?.find(attr => _.get(attr, 'name') === 'Styles')?.list || [];
      }
      return styles;   
    },
    maxChars() {
      return this.target.maxChars || 10000;
    },
    groupedThemeStyles() {
      const groups = {};
      this.themeStyles.forEach((style, index) => {
        if (!groups[style.name]) groups[style.name] = [];
        const label = this.getStyleOptionName(style);
        groups[style.name].push({ ...style, id: `${style.name}-${index}`, label });
      });

      return Object.entries(groups).map(([name, children]) => ({
        name,
        children
      }))
    },
    fontsUsedInStyles() {
      return this.themeStyles
        .map(style => _.get(style.list.find(attr => attr.type === 'font'), 'value'))
        .filter(font => !!font) || [];
    },
    relatedTemplateFields() {
      const fields = this.selectedInstance.template.fields
        .filter(field => RELATED_FIELD_TYPES.includes(field.type) && field.name !== this.target.templateField?.name)
        .map(field => field.name);
      
      return [
        { value: '', text: 'Select related field' },
        ...fields
      ];
    },
    alignToOptions() {
      const canvasObjects = this.selectedInstance.canvas.getObjects()
        .map(object => object.templateField?.name)
        .filter(object => object);
      
      const fields = this.selectedInstance.template.fields
        .filter(field => ALIGN_TO_FIELD_TYPES.includes(field.type) && canvasObjects.includes(field.name))
        .map(field => field.name);
      
      const additionalRectangles = this.selectedInstance.canvas.getObjects()
        .filter(object => object.element_id?.startsWith(ADDITIONAL_RECTANGLE_PREFIX_ELEMENT_ID) || object.id?.startsWith(ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID))
        .map(field => field.name);
      
      return [
        { value: '', text: 'Select a field for text alignment' },
        ...fields.concat(additionalRectangles)
      ];
    },
    isEditingDisabled() {
      return !editModeService.allowsContentEditing(this.target);
    },
    isTransformDisabled() {
      return !editModeService.allowsStructuralChanges(this.target);
    },
  },
  watch: {
    'target'(target, previousTarget) {
      this.unbindTargetEvents(previousTarget);
      this.bindTargetEvents(target);
      this.fontVal = this.findFont(target.fontFamily);
      this.config.fontSize = target.fontSize;
      this.config.height = target.height
      this.config.width = target.width
    },
    themeStyles: {
      immediate: true,
      handler() {
        this.matchSelectedStyle();
      }
    }
  },
  mounted() {
    this.config = {
      width: this.target.width,
      height: this.target.height,
      fontSize: this.target.fontSize,
    };

    this.autoPosition = !!this.target?.autoPosition
    this.fontVal = this.findFont(this.target.fontFamily);

    editorControlEventBroker.on(EVENTS.TEXT_SELECTION_CHANGED, this.handleTextSelectionChanged);
    this.bindTargetEvents(this.target);

    // this.target.on('modified', function () {
    //   if (isResizing) {
    //     vm.onChange('fontSize', this.fontSize);
    //     vm.$forceUpdate?.();
    //     isResizing = false;
    //   }
    // });

    // for style matching
    this.matchSelectedStyle();
  },
  beforeDestroy() {
    this.unbindTargetEvents(this.target);
    editorControlEventBroker.off(EVENTS.TEXT_SELECTION_CHANGED, this.handleTextSelectionChanged);
  },
  methods: {
    handleTextSelectionChanged({ fontFamily }) {
      this.fontVal = this.findFont(fontFamily);
    },
    bindTargetEvents(target) {
      if (!target?.on) return;

      this.targetSelectionChangedHandler = () => this.updateFontSizeFromSelection(target);
      this.targetScalingHandler = () => this.updateConfigFromScaling(target);
      target.on('selection:changed', this.targetSelectionChangedHandler);
      target.on('scaling', this.targetScalingHandler);
    },
    unbindTargetEvents(target) {
      if (!target?.off) return;

      if (this.targetSelectionChangedHandler) {
        target.off('selection:changed', this.targetSelectionChangedHandler);
      }
      if (this.targetScalingHandler) {
        target.off('scaling', this.targetScalingHandler);
      }
      this.targetSelectionChangedHandler = null;
      this.targetScalingHandler = null;
    },
    updateFontSizeFromSelection(target) {
      const { selectionStart, selectionEnd } = target;

      let style = target.getSelectionStyles(selectionStart)?.[0];
      if (selectionStart === selectionEnd && !style) {
        const lines = target.text.split('\n');
        let lineIndex = 0;
        let charCount = 0;

        for (; lineIndex < lines.length; lineIndex++) {
          const lineLength = lines[lineIndex].length + 1;
          if (selectionStart < charCount + lineLength) break;
          charCount += lineLength;
        }

        const charIndex = selectionStart - charCount;
        const isEndOfLine = charIndex === lines[lineIndex]?.length && charIndex > 0;
        const styleCharIndex = isEndOfLine ? charIndex - 1 : charIndex;

        style = target.styles?.[lineIndex]?.[styleCharIndex] ?? null;
      }
      this.config.fontSize = style?.fontSize ?? target.fontSize;
      this.$forceUpdate?.();
    },
    updateConfigFromScaling(target) {
      this.config = {
        ...this.config,
        width: Math.round(target.width * target.scaleX),
        height: Math.round(target.height * target.scaleY),
        fontSize: Math.round(target.fontSize * target.scaleY), // assuming uniform scaling for font size
      };
    },
    onChangeTextWeight (fontFamily) {
      this.onChange('fontFamily', fontFamily)
    },
    setFontFamily (fontFamily) {
      const fontFamilies = fontFamilyManager.getFontsFamily(fontFamily)
      if (fontFamilies) {
        this.fontFamilyWeights = fontFamilies.fonts
      } else {
        this.fontFamilyWeights = []
      }
    },
    toggleSectionHandler(sectionKey) {
      this.$emit('toggleSection', sectionKey);
    },
    diameterToRatio(diameter) {
      const min = 5;
      const max = 3000;
      return ((diameter - min) / (max - min)).toFixed(2);
    },
    ratioToDiameter(ratio) {
      const min = 5;
      const max = 3000;
      return Math.round(min + ratio * (max - min));
    },
    onRatioInput(event) {
      const ratio = parseFloat(event);
      if (isNaN(ratio)) return;
      const diameter = this.ratioToDiameter(ratio);
      this.onChangeThrottle('changeRadius', diameter);
    },
    betweenZeroAndOneFormatter(val) {
      return val > 1 ? 1 : (val < 0 ? 0 : +val)
    },
    onChangeThrottle: _.throttle(function (action, value) {
      this.onChange(action, value)
    }, 200),
    onChange (action, value) {
      const oldValue = this.target.text;
      editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
        action,
        value,
        maximizeFontSize: this.shouldMaximizeFontSize,
        keepLineBreaks: this.keepLineBreaks,
        useTemplateFontSize: this.useTemplateFontSize,
        oldValue
      })
      if (['fontFamily', 'fontSize', 'fontColor'].includes(action)) {
        this.matchSelectedStyle();
      }
    },
    onBlurText(event) {
      const action = event.currentTarget.dataset.action;
      // show dropdown only if align to button is clicked
      if (action !== 'align-to-btn') {
        this.onChange('redrawText', '');
        return;
      }

      this.redraw();
    },
    redraw() {
      if (!this.target.alignToField) {
        this.$refs.alignToFieldDropdown.show();
        
        return;
      }
      
      this.onChange('redrawText', '')
      
    },
    update({width, height, fontSize}) {
      this.config = {width, height, fontSize};
    },
    getAutoPositionValue(target) {
      if (Object.hasOwn(this.selectedInstance.data, `${target.element_id}_auto_position`)) {
        const value = this.selectedInstance.data[`${target.element_id}_auto_position`];
        return value === 'true' || value === true;
      }

      return false;
    },
    onChangeAutoPositionValue(value) {
      this.onChange('autoPosition', value);
    },
    toTitleCase (str) {
      return str.replace(/\w\S*/g, function (word) {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      })
    },

    formatText (value) {
      if (this.allCaps) {
        return value.toUpperCase()
      }

      if (this.titleCase) {
        return this.toTitleCase(value)
      }

      return value
    },

    handleTextInput (value) {
      this.onChange('inputText', this.formatText(value))
    },

    toggleTitleCase (enabled) {
      if (!enabled) {
        return
      }

      this.allCaps = false

      const updatedText = this.toTitleCase(this.text)
      this.onChange('inputText', updatedText)
    },

    toggleAllCaps (enabled) {
      if (!enabled) {
        return
      }

      this.titleCase = false

      const updatedText = this.text.toUpperCase()
      this.onChange('inputText', updatedText)
    },
    updateContainerSettings(event) {
      editorControlEventBroker.fire(EVENTS.EXTEND_CONTAINER_SETTINGS_CHANGED, {target: this.target, value: event});
    },
    onExtendContainer(event) {
      editorControlEventBroker.fire(EVENTS.EXTEND_CONTAINER, event);
    },
    handleFontChange(e) {
      this.setFontFamily(e.value);
      this.onChange('fontFamily', e.value);
    },
    findFont(value) {
      return _.find(this.getFonts, ['value', value]);
    },
    onFitTextWidthClick() {
      editorControlEventBroker.fire(EVENTS.FIT_TEXT_WIDTH, {
        object: this.target
      })
    },
    applyTextStyle(style) {
      if (!style  || style.$isLabel) return;
      style.list.forEach(attr => {
        if (attr.type === 'font') {
          const newFont = this.findFont(attr.value);
          if (!newFont) {
            toastr.warning('Font not found, please check theme Styles And Font settings');
            return;
          }
          this.fontVal = newFont;
          this.setFontFamily(newFont.value);
          this.onChange('fontFamily', newFont.value);
        } else if (attr.type === 'number') {
          // fallback if font size is not set
          const newSize = parseInt(attr.value) > 0 ? parseInt(attr.value) : 16;
          this.onChange('fontSize', newSize);
        } else if (attr.type === 'color') {
          // if color is not set
          const color = attr.value || '#000';
          this.onChange('fontColor', color);
        }
      });
    },
    getStylePreview(style) {
      if (style.$isLabel ) return {
        fontFamily: 'inherit',
        color: '#000',
        fontWeight: 'bold',
        size: 'inherit',
      }; 
      const font = _.get(style.list.find(attr => attr.type === 'font'), 'value') || 'inherit';
      const color = _.get(style.list.find(attr => attr.type === 'color'), 'value') || '#000';

      return {
        fontFamily: font,
        color: color,
      };
    },
    
    getStyleOptionName(style) {
      const font = _.get(style.list.find(attr => attr.type === 'font'), 'value') || 'inherit';
      const fontName = this.findFont(font)?.name || font;
      const size = _.get(style.list.find(attr => attr.type === 'number'), 'value');
      return size ? `[${size}px] ${fontName}` : fontName;
    },

    matchSelectedStyle() {
      if (!this.themeStyles || this.themeStyles.length === 0) return;

      const allGrouped = this.groupedThemeStyles.flatMap(group => group.children);
      
      const matchedStyle = allGrouped.find(style => {
        const font = _.get(style.list.find(attr => attr.type === 'font'), 'value');
        const size = parseInt(_.get(style.list.find(attr => attr.type === 'number'), 'value'));
        const color = _.get(style.list.find(attr => attr.type === 'color'), 'value')?.toLowerCase();
        const matchesFont = font === this.target.fontFamily;
        const matchesSize = isNaN(size) || size === parseInt(this.target.fontSize);
        const matchesColor = !color || color === this.target.fontColor?.toLowerCase();
        
        return matchesFont && matchesSize && matchesColor;
      });

      if (matchedStyle) {
        this.selectedStyle = matchedStyle;
      } else {
        this.selectedStyle = null;
      }
    },
    preventNegativeInput(event) {
      if (event.key === '-' || event.key === 'e') {
        event.preventDefault();
      }
    },
    focusTextarea() {
      this.$nextTick(() => {
        this.$refs.textEdit?.focus();
      });
    },
    async onCopy() {
      window.textFieldCopy = true;
      this.pasted = true;
      setTimeout(() => {
        this.pasted = false
      }, 1000);
      await copyPasteEventBroker.fire(COPY_PASTE_EVENTS.COPY, {
        canvas: this.target.canvas
      });

      copyPasteEventBroker.fire(COPY_PASTE_EVENTS.PASTE, {
        canvas: this.target.canvas
      });
    },
    async separateIntoTextBlocks () {
      if (this.isSeparating) return;

      const target = this.target;
      const lines = target.text?.split('\n') || [];

      if (lines.length <= 1) return;

      const nonEmptyLineIndices = [];
      for (let i = 0; i < lines.length; i++) {
        if (lines[i].trim().length > 0) {
          nonEmptyLineIndices.push(i);
        }
      }

      if (nonEmptyLineIndices.length <= 1) return;

      this.isSeparating = true;

      try {
        const canvas = target.canvas;
        const instanceId = canvas.instance_id;
        const scaleX = target.scaleX || 1;
        const scaleY = target.scaleY || 1;

        const lineTopOffsets = [0];
        for (let i = 0; i < lines.length - 1; i++) {
          const prevOffset = lineTopOffsets[i];
          const lineH = target.getHeightOfLine(i);
          lineTopOffsets.push(prevOffset + lineH);
        }

        const lineHeights = [];
        const lineLeftOffsets = [];
        for (let i = 0; i < lines.length; i++) {
          lineHeights[i] = target.getHeightOfLine(i);
          lineLeftOffsets[i] = typeof target._getLineLeftOffset === 'function'
            ? target._getLineLeftOffset(i)
            : 0;
        }

        const baseTop = target.top;
        const baseLeft = target.left;
        const objectWidth = target.width;
        const textAlign = (target.textAlign || 'left').split(',')[0];
        const fontFamily = target.fontFamily;
        const fontSize = target.fontSize;
        const fontColor = target.fontColor || target.fill || '#000';
        const lineHeight = target.lineHeight || 1;
        const charSpacing = target.charSpacing || 0;
        const opacity = target.opacity ?? 1;
        const angle = target.angle || 0;
        const targetName = target.name || target.templateField?.name || 'Text';
        const targetStyles = target.styles ? JSON.parse(JSON.stringify(target.styles)) : {};

        const lineOptions = [];
        for (let idx = 0; idx < nonEmptyLineIndices.length; idx++) {
          const lineIndex = nonEmptyLineIndices[idx];
          const lineText = lines[lineIndex];
          const lineTopOffset = lineTopOffsets[lineIndex] * scaleY;
          const lineTop = baseTop + lineTopOffset;
          const lineLeft = baseLeft + (lineLeftOffsets[lineIndex] * scaleX);
          const lineH = lineHeights[lineIndex];
          const lineStyles = targetStyles[lineIndex] || {};

          let effectiveFontSize = fontSize;
          if (lineStyles && Object.keys(lineStyles).length > 0) {
            const firstCharStyle = lineStyles[0] || lineStyles[Object.keys(lineStyles)[0]];
            if (firstCharStyle?.fontSize) {
              effectiveFontSize = firstCharStyle.fontSize;
            }
          }

          lineOptions.push({
            text: lineText,
            _offset_x: String(lineLeft),
            _offset_y: String(lineTop),
            _width: String(Math.round(objectWidth * scaleX)),
            _height: String(Math.round(lineH * scaleY)),
            _font: fontFamily,
            _fontsize: String(Math.round(effectiveFontSize * scaleY)),
            _color: fontColor,
            _alignment: textAlign,
            _leading: String(lineHeight),
            _tracking: charSpacing,
            _opacity: String(opacity),
            _angle: String(angle),
            _styles: lineStyles,
            name: `${targetName} - Line ${idx + 1}`,
          });
        }

        editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
          action: 'delete',
          value: null,
          skipConfirmation: true,
        });

        await new Promise(resolve => setTimeout(resolve, 200));

        for (const options of lineOptions) {
          await addAddField('Multi Line Text', instanceId, options);
        }

      } catch (error) {
        console.error('Error separating text blocks:', error);
        toastr.error('Failed to separate text blocks');
      } finally {
        this.isSeparating = false;
      }
    },
  }
}
</script>

<style scoped lang="scss">
.btn-sm {
  min-width: 120px;
}

.align-to-btn {
  min-width: 200px;
}

::v-deep .pcr-app{
    left: 28px !important;
}

.align-options-dropdown {
  ::v-deep .dropdown-toggle {
    border-left: 1px solid rgba(255, 255, 255, 0.3);
    padding: 0.25rem 0.5rem;
    
    &::after {
      display: none;
    }
  }
  
  ::v-deep .dropdown-menu {
    min-width: 200px;
    padding: 0.5rem;
  }
}

.btn-group {
  .btn:first-child {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
  
  .dropdown-toggle {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
}

.b-dropdown-form{
  padding: .5rem 1rem;
}

.position-warning {
  font-size: 0.9rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  
  .warning-actions {
    .btn-sm {
      display: flex !important;
      align-items: center !important;
      min-width: auto;
      padding: 0.25rem 0.75rem;
      color: #000 !important;
    }
  }
}
.theme-font-separator {
  display: block;
  position:relative;
  &:after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -15px;
    height: 1px;
    border-bottom: 1px dashed rgba(0, 0, 0, 0.3);
  } 
}
.text-editor-actions {
  flex-wrap: wrap;
  gap: 5px;

  & > * {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 2rem;
    min-width: 2rem;
    border: 1px solid #e2e8f0;
    border-radius: .5rem;

    &:hover {
      background-color: #f1f5f9;
    }
  }
}
.color-checkbox-container{
  padding-right: 2px;
  padding-left: 2px;
}

.color-checkbox-container .pickr{
  margin-top: -8px;
}

.color-checkbox-container .pickr button{
  width: 18px !important;
  height: 18px !important;
}

.color-checkbox-container .bi-copy{
  font-size: 12px !important;
  margin-top: 0px  !important;
  padding-left: 6px !important;
}

.apply-to-all-text-dropdwon-section{
  bottom: 44px; 
  z-index: 999;
}
.text-case-checkboxes {
  display: flex;
  align-items: center;
  gap: 12px;
}

.text-case-checkboxes ::v-deep .custom-control {
  display: flex;
  align-items: center;
  min-height: auto;
}

.text-case-checkboxes ::v-deep .custom-control-label {
  display: flex;
  align-items: center;
  margin-bottom: 0;
}

.text-case-checkboxes ::v-deep .custom-control-label::before,
.text-case-checkboxes ::v-deep .custom-control-label::after {
  top: 50%;
  transform: translateY(-50%);
}
</style>
